import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Loader2, Scale, ArrowRight, TrendingUp, Users2, FileDown, CheckSquare, Sparkles, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currentYear = new Date().getFullYear();

const STATUS_META = {
  seleccionado: { label: "Seleccionado", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  candidato: { label: "Candidato", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  descartado: { label: "Descartado", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  sin_estado: { label: "Sin estado", cls: "bg-white text-slate-500 border-slate-200" },
};

export default function AuditoriaGlobalPage() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // {project_id: true}
  const [bulkStatus, setBulkStatus] = useState("candidato");
  const [applying, setApplying] = useState(false);
  const [recs, setRecs] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [applyingRec, setApplyingRec] = useState(null); // line name currently being applied
  const navigate = useNavigate();

  const loadRecs = async () => {
    setLoadingRecs(true);
    try {
      const r = await fetch(`${API}/pg07/auditoria/recommendations?next_year=${year + 1}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setRecs(await r.json());
    } finally { setLoadingRecs(false); }
  };

  const load = () => {
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API}/pg07/auditoria/summary?year=${year}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    })();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token, year]);

  const selectedIds = Object.keys(selected).filter(k => selected[k]);

  const applyBulk = async () => {
    if (!selectedIds.length) return;
    setApplying(true);
    try {
      const r = await fetch(`${API}/pg07/auditoria/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          project_ids: selectedIds,
          auditoria_status: bulkStatus,
          auditoria_year: year,
          auditoria_fecha: new Date().toISOString().slice(0, 10),
        }),
      });
      if (r.ok) {
        const j = await r.json();
        toast.success(`${j.modified} proyectos actualizados a "${bulkStatus}"`);
        setSelected({});
        load();
      } else {
        toast.error("Error");
      }
    } finally { setApplying(false); }
  };

  const downloadPDF = async () => {
    const r = await fetch(`${API}/pg07/auditoria/pdf?year=${year}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Error generando PDF"); return; }
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `PG07_auditoria_${year}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const applyRecommendation = async (rec) => {
    const targetYear = recs?.next_year || year + 1;
    if (!rec.all_project_ids?.length) {
      toast.error("Sin proyectos para esta línea");
      return;
    }
    if (!window.confirm(`Marcar ${rec.all_project_ids.length} proyecto(s) de "${rec.business_line}" como candidatos para la auditoría ${targetYear}?`)) return;
    setApplyingRec(rec.business_line);
    try {
      const r = await fetch(`${API}/pg07/auditoria/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          project_ids: rec.all_project_ids,
          auditoria_status: "candidato",
          auditoria_year: targetYear,
          auditoria_fecha: new Date().toISOString().slice(0, 10),
        }),
      });
      if (r.ok) {
        const j = await r.json();
        toast.success(`${j.modified} proyectos de "${rec.business_line}" marcados como candidatos ${targetYear}`);
        setYear(targetYear);
        loadRecs();
      } else {
        toast.error("Error aplicando recomendación");
      }
    } finally { setApplyingRec(null); }
  };

  return (
    <div className="space-y-6" data-testid="auditoria-global-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold">PG-07</div>
          <h1 className="text-3xl font-black flex items-center gap-2"><Scale className="w-7 h-7 text-violet-600" /> Auditoría anual · Vista global</h1>
          <p className="text-sm text-muted-foreground mt-1">Candidatos y seleccionados del año, con ranking por línea de servicio.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-muted-foreground">Año:</label>
          <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value) || currentYear)}
                 className="w-24 h-9 px-2 rounded border text-sm" data-testid="auditoria-year-filter" />
          <Button variant="outline" size="sm" onClick={downloadPDF} data-testid="auditoria-pdf-btn">
            <FileDown className="w-3.5 h-3.5 mr-1" />PDF Comité
          </Button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-2 p-3 rounded border border-violet-300 bg-violet-50 dark:bg-violet-950/30" data-testid="auditoria-bulk-toolbar">
          <CheckSquare className="w-4 h-4 text-violet-700" />
          <span className="text-sm font-semibold text-violet-900 dark:text-violet-200">{selectedIds.length} proyecto(s) seleccionado(s)</span>
          <span className="text-xs text-muted-foreground">→ marcar como</span>
          <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)} className="h-8 px-2 rounded border text-xs" data-testid="auditoria-bulk-status-select">
            <option value="candidato">Candidato</option>
            <option value="seleccionado">Seleccionado</option>
            <option value="descartado">Descartado</option>
            <option value="">(sin estado)</option>
          </select>
          <Button size="sm" onClick={applyBulk} disabled={applying} data-testid="auditoria-bulk-apply">
            {applying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Aplicar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected({})} data-testid="auditoria-bulk-clear">Limpiar</Button>
        </div>
      )}

      {loading ? <div className="flex justify-center p-10"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div> : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="auditoria-stats">
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Seleccionados</div><div className="text-3xl font-black text-emerald-700">{data.stats.seleccionado}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Candidatos</div><div className="text-3xl font-black text-amber-700">{data.stats.candidato}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Descartados</div><div className="text-3xl font-black text-slate-500">{data.stats.descartado}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Sin estado</div><div className="text-3xl font-black text-slate-400">{data.stats.sin_estado}</div></CardContent></Card>
          </div>

          {(data.line_ranking || []).length > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-violet-600" /> Ranking por línea de servicio</h3>
                  <Button variant="outline" size="sm" onClick={loadRecs} data-testid="auditoria-recommend-btn">
                    <Sparkles className="w-3.5 h-3.5 mr-1 text-amber-600" />
                    Sugerir para {year + 1}
                  </Button>
                </div>
                <div className="space-y-2" data-testid="auditoria-line-ranking">
                  {data.line_ranking.map((l, i) => (
                    <div key={l.business_line} className="border rounded p-3 space-y-1.5" data-testid={`auditoria-line-${i}`}>
                      <div className="flex items-center gap-3">
                        <span className="w-6 text-muted-foreground">{i + 1}</span>
                        <span className="flex-1 font-semibold">{l.business_line}</span>
                        <Badge variant="outline" className="text-[10px]">{l.total} proy.</Badge>
                        {l.seleccionado > 0 && <Badge className="text-[10px] bg-emerald-100 text-emerald-800">{l.seleccionado} sel.</Badge>}
                        {l.candidato > 0 && <Badge className="text-[10px] bg-amber-100 text-amber-800">{l.candidato} cand.</Badge>}
                      </div>
                      <div className="flex flex-wrap gap-1 pl-8">
                        {l.projects.map(p => (
                          <button key={p.id} onClick={() => navigate(`/pg07/extras?project=${p.id}`)} className="text-[11px] px-1.5 py-0.5 rounded border hover:bg-slate-50" data-testid={`auditoria-line-project-${p.id}`}>
                            {p.name}
                            {p.status && <span className="ml-1 text-[9px] text-muted-foreground">· {p.status}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {(loadingRecs || recs) && (
            <Card className="border-amber-200 bg-amber-50/40 dark:bg-amber-950/10">
              <CardContent className="p-4" data-testid="auditoria-recommendations">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <h3 className="text-sm font-bold">Recomendaciones para auditoría {recs?.next_year || year + 1}</h3>
                  <span className="text-xs text-muted-foreground">Ventana {recs?.window_start} — {(recs?.next_year || 0) - 1}</span>
                </div>
                {loadingRecs ? <Loader2 className="w-4 h-4 animate-spin text-amber-600" /> : (
                  <div className="space-y-2">
                    {(recs?.recommendations || []).map((r, i) => (
                      <div key={r.business_line} className={`border rounded p-3 flex flex-wrap items-center gap-2 ${r.priority === "alta" ? "border-red-300 bg-red-50/40" : r.priority === "media" ? "border-amber-300 bg-amber-50/30" : "border-slate-200"}`} data-testid={`auditoria-rec-${i}`}>
                        <span className="w-6 text-xs text-muted-foreground">{i + 1}</span>
                        <span className="flex-1 font-semibold">{r.business_line}</span>
                        <Badge variant="outline" className="text-[10px]">{r.total_projects} proy.</Badge>
                        <Badge className={`text-[10px] ${r.priority === "alta" ? "bg-red-100 text-red-800" : r.priority === "media" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                          Prioridad {r.priority}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-800 h-7 text-xs"
                          onClick={() => applyRecommendation(r)}
                          disabled={applyingRec === r.business_line || !r.all_project_ids?.length}
                          data-testid={`auditoria-rec-apply-${i}`}
                        >
                          {applyingRec === r.business_line ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckSquare className="w-3 h-3 mr-1" />}
                          Aplicar {recs?.next_year || year + 1}
                        </Button>
                        <span className="text-xs text-muted-foreground w-full pl-8">{r.reason}</span>
                        {(r.audited_years || []).length > 0 && (
                          <span className="text-[10px] text-muted-foreground pl-8 inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />Años auditados: {r.audited_years.join(", ")}
                          </span>
                        )}
                      </div>
                    ))}
                    {!recs?.recommendations?.length && <div className="text-sm text-muted-foreground italic">Sin datos suficientes.</div>}
                  </div>
                )}
              </CardContent>
            </Card>
          )}


          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="auditoria-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="w-8 px-2">
                      <input type="checkbox"
                        checked={data.rows.length > 0 && selectedIds.length === data.rows.length}
                        onChange={e => setSelected(e.target.checked ? Object.fromEntries(data.rows.map(r => [r.id, true])) : {})}
                        data-testid="auditoria-select-all" />
                    </th>
                    <th className="text-left px-4 py-2">Proyecto</th>
                    <th className="text-left px-3 py-2">PM</th>
                    <th className="text-left px-3 py-2">Estado auditoría</th>
                    <th className="text-left px-3 py-2">Fecha</th>
                    <th className="text-left px-3 py-2">Notas</th>
                    <th className="w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).map((r, i) => {
                    const s = r.auditoria_status || "sin_estado";
                    const meta = STATUS_META[s];
                    return (
                      <tr key={r.id} className={`border-t hover:bg-slate-50 dark:hover:bg-slate-800/50 ${selected[r.id] ? "bg-violet-50/50 dark:bg-violet-950/20" : ""}`} data-testid={`auditoria-row-${i}`}>
                        <td className="px-2 text-center">
                          <input type="checkbox" checked={!!selected[r.id]}
                            onChange={e => setSelected(prev => ({ ...prev, [r.id]: e.target.checked }))}
                            data-testid={`auditoria-row-check-${i}`} />
                        </td>
                        <td className="px-4 py-3 font-medium">{r.name}</td>
                        <td className="px-3 py-3 text-xs">{r.pm_email || "—"}</td>
                        <td className="px-3 py-3">
                          <Badge className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                        </td>
                        <td className="px-3 py-3 text-xs font-mono">{r.auditoria_fecha || "—"}</td>
                        <td className="px-3 py-3 text-xs text-muted-foreground truncate max-w-md">{r.auditoria_notes || "—"}</td>
                        <td className="px-2 py-3">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/pg07/extras?project=${r.id}`)} data-testid={`auditoria-edit-${i}`}>
                            Editar <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {!data.rows?.length && (<tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Sin proyectos.</td></tr>)}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
